import React from 'react';
import type { TFunction } from 'i18next';
import type { PlanStudioPlanStatus } from './types';

export type ProfilePlanListView = PlanStudioPlanStatus | 'history';

export const PROFILE_PLAN_STATUS_FILTERS: PlanStudioPlanStatus[] = [
  'active',
  'inactive',
  'archived',
];

export const PROFILE_PLAN_LIST_VIEWS: ProfilePlanListView[] = [
  ...PROFILE_PLAN_STATUS_FILTERS,
  'history',
];

const planStatusClassNames: Record<PlanStudioPlanStatus, string> = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  inactive: 'border-amber-200 bg-amber-50 text-amber-700',
  archived: 'border-slate-200 bg-slate-100 text-slate-600',
};

const planListViewClassNames: Record<ProfilePlanListView, string> = {
  ...planStatusClassNames,
  history: 'border-sky-200 bg-sky-50 text-sky-700',
};

export const planListViewLabelKey = (view: ProfilePlanListView): string =>
  view === 'history'
    ? 'planAhead.planListViews.history'
    : `planAhead.planStatuses.${view}`;

interface PlanListViewTabsProps<TView extends ProfilePlanListView> {
  availableViews: readonly TView[];
  selectedView: TView;
  onViewChange: (view: TView) => void;
  t: TFunction;
  counts?: Partial<Record<TView, number>>;
  className?: string;
  isDark?: boolean;
}

export function PlanListViewTabs<TView extends ProfilePlanListView>({
  availableViews,
  selectedView,
  onViewChange,
  t,
  counts,
  className = '-mx-1 mt-3',
  isDark = false,
}: PlanListViewTabsProps<TView>): React.JSX.Element {
  return (
    <div
      className={`${className} flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
    >
      {availableViews.map((view) => {
        const selected = selectedView === view;
        const count = counts?.[view];
        const label = t(planListViewLabelKey(view));

        return (
          <button
            key={view}
            type="button"
            onClick={() => onViewChange(view)}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
              selected
                ? isDark
                  ? view === 'active'
                    ? 'border-emerald-800/70 bg-emerald-950/40 text-emerald-300'
                    : view === 'inactive'
                      ? 'border-amber-800/70 bg-amber-950/40 text-amber-300'
                      : view === 'archived'
                        ? 'border-[#48484a] bg-[#2c2c2e] text-[#c7c7cc]'
                        : 'border-sky-800/70 bg-sky-950/40 text-sky-300'
                  : planListViewClassNames[view]
                : isDark
                  ? 'border-[#3a3a3c] bg-[#1c1c1e] text-[#8e8e93] hover:bg-[#2c2c2e] hover:text-[#f5f5f7]'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
            }`}
          >
            {count === undefined ? label : `${label} (${count})`}
          </button>
        );
      })}
    </div>
  );
}
